import { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteMarMedication, listMarMedications } from '../../../services/api';
import type {
  MarMedicationResponse,
  PatientMedicationResponse,
} from '../../../services/api';
import { useToast } from '../../../hooks/useToastContext';

interface UseMarRecordsOptions {
  patientId: string;
  /** Selected visit; MAR is scoped to it. When undefined, MAR is inactive. */
  visitId?: string;
  /**
   * Called after a MAR record is created/edited/deleted so the caller can
   * refresh the medication list (its `has_administration_records` flag changes).
   */
  onRecordsChanged: () => void;
}

export interface UseMarRecords {
  marEntries: MarMedicationResponse[];
  /** MAR entries grouped by the profile medication they reference. */
  marByMedicationId: Map<string, MarMedicationResponse[]>;
  /** Row/summary affordances. */
  actions: {
    onAddRecord: (med: PatientMedicationResponse) => void;
    onEditRecord: (entry: MarMedicationResponse) => void;
    onDeleteRecord: (entry: MarMedicationResponse) => void;
  };
  /** State for the add/edit record dialog. */
  recordDialog: {
    open: boolean;
    setOpen: (open: boolean) => void;
    target: PatientMedicationResponse | null;
    editing: MarMedicationResponse | null;
    onSaved: () => void;
  };
  /** State for the delete-confirmation dialog. */
  deleteDialog: {
    pending: MarMedicationResponse | null;
    cancel: () => void;
    confirm: () => void;
    isDeleting: boolean;
  };
}

/**
 * Owns all Medication Administration Record state for a patient + visit:
 * fetching, grouping, and the add/edit/delete dialog lifecycle.
 */
export function useMarRecords({
  patientId,
  visitId,
  onRecordsChanged,
}: UseMarRecordsOptions): UseMarRecords {
  const { error: showError } = useToast();
  const [marEntries, setMarEntries] = useState<MarMedicationResponse[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [target, setTarget] = useState<PatientMedicationResponse | null>(null);
  const [editing, setEditing] = useState<MarMedicationResponse | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<MarMedicationResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEntries = useCallback(async () => {
    if (!visitId) {
      setMarEntries([]);
      return;
    }
    try {
      // The client is configured without `throwOnError`, so an HTTP failure
      // comes back as a populated `error` rather than a thrown exception —
      // inspect it explicitly so a failed load isn't shown as "no records".
      const response = await listMarMedications({
        path: { patient_id: patientId, visit_id: visitId },
      });
      if (response.error) {
        console.error(
          'Failed to fetch administration records:',
          response.error
        );
        showError('Failed to load administration records. Please try again.');
        setMarEntries([]);
        return;
      }
      setMarEntries(response.data ?? []);
    } catch (err) {
      console.error('Failed to fetch administration records:', err);
      showError('Failed to load administration records. Please try again.');
      setMarEntries([]);
    }
  }, [patientId, visitId, showError]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const marByMedicationId = useMemo(() => {
    const map = new Map<string, MarMedicationResponse[]>();
    for (const entry of marEntries) {
      const list = map.get(entry.patient_medication_id) ?? [];
      list.push(entry);
      map.set(entry.patient_medication_id, list);
    }
    return map;
  }, [marEntries]);

  const onAddRecord = useCallback((med: PatientMedicationResponse) => {
    setTarget(med);
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const onEditRecord = useCallback((entry: MarMedicationResponse) => {
    setTarget(null);
    setEditing(entry);
    setDialogOpen(true);
  }, []);

  const handleChanged = useCallback(() => {
    fetchEntries();
    onRecordsChanged();
  }, [fetchEntries, onRecordsChanged]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete || !visitId) return;
    setIsDeleting(true);
    try {
      // The client is configured without `throwOnError`, so an HTTP failure
      // comes back as a populated `error` rather than a thrown exception.
      const response = await deleteMarMedication({
        path: {
          patient_id: patientId,
          visit_id: visitId,
          mar_id: pendingDelete.id,
        },
      });
      if (response.error) {
        console.error(
          'Failed to delete administration record:',
          response.error
        );
        showError('Failed to delete administration record. Please try again.');
        return;
      }
      setPendingDelete(null);
      handleChanged();
    } catch (err) {
      console.error('Failed to delete administration record:', err);
      showError('Failed to delete administration record. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDelete, visitId, patientId, handleChanged, showError]);

  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  const actions = useMemo(
    () => ({
      onAddRecord,
      onEditRecord,
      onDeleteRecord: setPendingDelete,
    }),
    [onAddRecord, onEditRecord]
  );

  const recordDialog = useMemo(
    () => ({
      open: dialogOpen,
      setOpen: setDialogOpen,
      target,
      editing,
      onSaved: handleChanged,
    }),
    [dialogOpen, target, editing, handleChanged]
  );

  const deleteDialog = useMemo(
    () => ({
      pending: pendingDelete,
      cancel: cancelDelete,
      confirm: confirmDelete,
      isDeleting,
    }),
    [pendingDelete, cancelDelete, confirmDelete, isDeleting]
  );

  return useMemo(
    () => ({
      marEntries,
      marByMedicationId,
      actions,
      recordDialog,
      deleteDialog,
    }),
    [marEntries, marByMedicationId, actions, recordDialog, deleteDialog]
  );
}
